/**
 * OverlayManager (ui-ux §11.2): one place that opens top-layer overlays in priority order
 * `sheet 40 < modal 50 < toast 60 < tooltip 70 < coach 80 < fatal 90 < debug 100`, keeps at most one modal (or modal
 * sheet) visible with a FIFO queue (confirms opened from a modal may stack to depth 2), re-appends higher-priority
 * popovers when a lower one opens, and lets `fatal` close everything. History integration (back closes the top
 * overlay) is F1's router: it calls {@link OverlayManager.closeTop}. Owner: UI/Visual (F3).
 */
export type OverlayKind = 'sheet' | 'modal' | 'toast' | 'tooltip' | 'coach' | 'fatal' | 'debug';
export const OVERLAY_PRIORITY: Readonly<Record<OverlayKind, number>> = { sheet: 40, modal: 50, toast: 60, tooltip: 70, coach: 80, fatal: 90, debug: 100 };

export interface OverlayEntry {
  id: string;
  kind: OverlayKind;
  /** A `<dialog>` (sheet / modal) or a `[popover]` element. */
  el: HTMLElement;
  /** Modal entries block others (scrim + inert). */
  modal: boolean;
  /** Opened from another modal (a confirm): may stack to depth 2. */
  child?: boolean;
  /** Called when the manager closes it (back, Esc, fatal). Return false to refuse (mandatory overlays). */
  onRequestClose?: () => boolean | void;
}

export class OverlayManager {
  private readonly open: OverlayEntry[] = [];
  private readonly queue: OverlayEntry[] = [];
  private blocked = false;

  /** Opens (or queues) an overlay. Returns 'open' | 'queued' | 'blocked'. */
  show(e: OverlayEntry): 'open' | 'queued' | 'blocked' {
    if (this.blocked && e.kind !== 'debug') return 'blocked';
    if (e.kind === 'fatal') { this.closeAll(); this.blocked = true; }
    if (e.modal) {
      const modals = this.open.filter((o) => o.modal);
      if (modals.length > 0 && !(e.child && modals.length < 2)) { this.queue.push(e); return 'queued'; }
    }
    this.mount(e);
    this.open.push(e);
    this.raiseAbove(OVERLAY_PRIORITY[e.kind]);
    return 'open';
  }

  /** Closes one overlay by id and opens the next queued modal. */
  close(id: string): void {
    const i = this.open.findIndex((o) => o.id === id);
    if (i < 0) { const q = this.queue.findIndex((o) => o.id === id); if (q >= 0) this.queue.splice(q, 1); return; }
    const [e] = this.open.splice(i, 1);
    this.unmount(e);
    if (e.modal && !this.open.some((o) => o.modal)) {
      const next = this.queue.shift();
      if (next) this.show(next);
    }
  }

  /** Back / Esc: asks the top closable overlay to close. Returns true when one handled it. */
  closeTop(): boolean {
    for (let i = this.open.length - 1; i >= 0; i--) {
      const e = this.open[i];
      if (e.kind === 'toast' || e.kind === 'debug') continue;
      const ok = e.onRequestClose ? e.onRequestClose() : undefined;
      if (ok === false) return true;              // a mandatory overlay swallows back
      this.close(e.id);
      return true;
    }
    return false;
  }

  closeAll(): void { for (const e of [...this.open]) this.close(e.id); this.queue.length = 0; }
  list(): Array<{ id: string; kind: OverlayKind }> { return this.open.map((o) => ({ id: o.id, kind: o.kind })); }
  queued(): string[] { return this.queue.map((o) => o.id); }
  get modalOpen(): boolean { return this.open.some((o) => o.modal); }

  private mount(e: OverlayEntry): void {
    if (typeof document === 'undefined') return;
    if (!e.el.isConnected) document.body.appendChild(e.el);
    if (e.el instanceof HTMLDialogElement) {
      if (!e.el.open) { if (e.modal) e.el.showModal(); else e.el.show(); }
      if (e.modal) e.el.dataset.modal = '';
    } else if (e.el.hasAttribute('popover')) {
      try { e.el.showPopover(); } catch { /* already open */ }
    }
  }

  private unmount(e: OverlayEntry): void {
    if (typeof document === 'undefined') return;
    if (e.el instanceof HTMLDialogElement) { if (e.el.open) e.el.close(); }
    else if (e.el.hasAttribute('popover')) { try { e.el.hidePopover(); } catch { /* closed */ } }
  }

  /** Re-appends open overlays of a higher priority so they stay above the new one in the top layer. */
  private raiseAbove(p: number): void {
    if (typeof document === 'undefined') return;
    const higher = this.open.filter((o) => OVERLAY_PRIORITY[o.kind] > p).sort((a, b) => OVERLAY_PRIORITY[a.kind] - OVERLAY_PRIORITY[b.kind]);
    for (const e of higher) {
      if (e.el instanceof HTMLDialogElement) continue;           // dialogs cannot be re-raised without closing
      if (!e.el.hasAttribute('popover')) continue;
      try { e.el.hidePopover(); e.el.showPopover(); } catch { /* ignore */ }
    }
  }
}

/** The app-wide instance (F1 may construct its own and pass it to screens; components default to this one). */
export const overlays = new OverlayManager();
